import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Popover from "@material-ui/core/Popover";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";

import CloseIcon from "@material-ui/icons/Close";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import StrikethroughSIcon from "@material-ui/icons/StrikethroughS";
import AddIcon from "@material-ui/icons/Add";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { getTokens } from "../../styles/tokens";
import {
  BILLING_VARIABLES,
  CATEGORIES,
  EDITABLE_STATUSES,
  HEADER_FORMATS,
  LANGUAGES,
  LIMITS,
  PARAMETER_FORMATS,
  apiErrorMessage,
  convertVariables,
  emptyForm,
  extractVariables,
  fromApiTemplate,
  nextPositionalIndex,
  toPayload,
  toSnakeCase,
  validateForm,
} from "../../pages/MetaTemplates/templateUtils";
import StatusChip from "../../pages/MetaTemplates/StatusChip";
import PhonePreview from "./PhonePreview";
import ButtonsEditor from "./ButtonsEditor";

const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    // Altura cheia (o MUI limita a 100% - 64px): o modal não pula conforme as seções crescem
    paper: { height: "100%" },
    dialogTitle: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    titleWrap: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    titleText: {
      margin: 0,
      minWidth: 0,
      font: "inherit",
      letterSpacing: "inherit",
      lineHeight: 1.35,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    // Formulário | prévia fixa; abaixo de 960 px empilha e o conteúdo rola inteiro
    content: {
      display: "flex",
      minHeight: 0,
      overflow: "hidden",
      [theme.breakpoints.down("sm")]: {
        display: "block",
        overflowY: "auto",
        ...theme.scrollbarStyles,
      },
    },
    formCol: {
      flex: 1,
      minWidth: 0,
      padding: "20px 24px 24px",
      overflowY: "auto",
      ...theme.scrollbarStyles,
      [theme.breakpoints.down("sm")]: {
        overflowY: "visible",
        padding: 16,
      },
    },
    previewCol: {
      width: 360,
      flexShrink: 0,
      borderLeft: `1px solid ${t.line}`,
      background: t.muted,
      padding: "20px 24px 24px",
      overflowY: "auto",
      ...theme.scrollbarStyles,
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        borderLeft: "none",
        borderTop: `1px solid ${t.line}`,
        overflowY: "visible",
        padding: 16,
      },
    },
    previewTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: t.text,
      margin: "0 0 14px",
    },
    alert: { marginBottom: 16 },
    section: {
      "& + &": {
        marginTop: 22,
        paddingTop: 20,
        borderTop: `1px solid ${t.line}`,
      },
    },
    sectionHead: { marginBottom: 14 },
    sectionTitle: { margin: 0, fontSize: 13, fontWeight: 600, lineHeight: 1.4, color: t.text },
    sectionHelp: { margin: "2px 0 0", fontSize: 12, lineHeight: 1.5, color: t.textMuted },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
      gap: 16,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    full: { gridColumn: "1 / -1" },
    span2: { gridColumn: "span 2", [theme.breakpoints.down("xs")]: { gridColumn: "1 / -1" } },
    span3: { gridColumn: "span 3", [theme.breakpoints.down("xs")]: { gridColumn: "1 / -1" } },
    span4: { gridColumn: "span 4", [theme.breakpoints.down("xs")]: { gridColumn: "1 / -1" } },
    field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
    labelRow: { display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 },
    label: { fontSize: 12.5, fontWeight: 600, color: t.textSecondary, overflowWrap: "anywhere" },
    labelMono: { fontFamily: MONO, fontWeight: 500 },
    help: { fontSize: 12, lineHeight: 1.5, color: t.textMuted },
    toolbar: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 4,
      "& .MuiIconButton-root:not(.Mui-disabled)": { color: t.textSecondary },
    },
    toolbarSep: { width: 1, height: 20, background: t.line, margin: "0 6px" },
    counter: {
      marginLeft: "auto",
      flex: "none",
      color: t.textMuted,
      fontSize: 12,
      fontVariantNumeric: "tabular-nums",
    },
    counterOver: { color: t.semantic.error },
    chips: {
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
    },
    chip: { fontFamily: MONO, fontWeight: 500, cursor: "pointer" },
    stack: { display: "flex", flexDirection: "column", gap: 16 },
    examplesGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 12,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    popover: { padding: 16, width: 340, maxWidth: "calc(100vw - 32px)" },
    popoverRow: { display: "flex", alignItems: "center", gap: 8 },
    saveWrapper: { position: "relative" },
    progress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -10,
      marginLeft: -10,
    },
  };
});

const tr = (key, opts) => i18n.t(`metaTemplates.editor.${key}`, opts);

/** Campo do modal padrão: rótulo acima (contador à direita), ajuda curta abaixo. */
const Field = ({ classes, label, id, labelId, required, mono, counter, help, className, children }) => (
  <div className={`${classes.field}${className ? ` ${className}` : ""}`}>
    <div className={classes.labelRow}>
      <label id={labelId} htmlFor={id} className={`${classes.label}${mono ? ` ${classes.labelMono}` : ""}`}>
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      {counter}
    </div>
    {children}
    {help && <div className={classes.help}>{help}</div>}
  </div>
);

const SectionHead = ({ classes, title, help }) => (
  <div className={classes.sectionHead}>
    <h3 className={classes.sectionTitle}>{title}</h3>
    {help && <p className={classes.sectionHelp}>{help}</p>}
  </div>
);

/**
 * Editor de template Meta no modal padrão do painel: formulário em seções à
 * esquerda e prévia em moldura de celular fixa à direita (abaixo de 960 px o
 * modal ocupa a tela e empilha em coluna única).
 */
const MetaTemplateEditor = ({ open, onClose, template, whatsappId, whatsapps, onSaved }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [form, setForm] = useState(() => emptyForm(whatsappId));
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");
  const [clientErrors, setClientErrors] = useState([]);
  const [varAnchor, setVarAnchor] = useState(null);
  const [varName, setVarName] = useState("");
  const bodyRef = useRef(null);
  const formColRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm(template ? fromApiTemplate(template) : emptyForm(whatsappId));
    setApiError("");
    setClientErrors([]);
    setSaving(false);
    setVarAnchor(null);
    setVarName("");
  }, [open, template, whatsappId]);

  const isExisting = !!form.id;
  const isLocked = isExisting; // a Meta nunca deixa mudar nome/idioma
  const showEditNotice = isExisting && EDITABLE_STATUSES.includes(form.status);
  const showPendingNotice = isExisting && form.status === "PENDING";

  const set = useCallback((patch) => setForm((prev) => ({ ...prev, ...patch })), []);
  const field = (name) => (e) => set({ [name]: e.target.value });

  const bodyVars = extractVariables(form.bodyText);
  const headerVar = extractVariables(form.headerText)[0];
  const isNamed = form.parameterFormat === "NAMED";

  const selectedWhatsapp = (whatsapps || []).find(
    (w) => String(w.id) === String(form.whatsappId)
  );

  const scrollTop = () => {
    if (formColRef.current) formColRef.current.scrollTop = 0;
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleParameterFormat = (e) => {
    const toFormat = e.target.value;
    if (toFormat === form.parameterFormat) return;
    setForm((prev) => {
      const body = convertVariables(prev.bodyText, prev.bodyExamples, toFormat);
      const hv = extractVariables(prev.headerText)[0];
      const header = convertVariables(
        prev.headerText,
        hv ? { [hv]: prev.headerVarExample } : {},
        toFormat
      );
      const newHv = extractVariables(header.text)[0];
      return {
        ...prev,
        parameterFormat: toFormat,
        bodyText: body.text,
        bodyExamples: body.examples,
        headerText: header.text,
        headerVarExample: newHv ? header.examples[newHv] || "" : "",
      };
    });
  };

  /** Insere `snippet` na posição do cursor do corpo (ou no fim). */
  const insertIntoBody = (snippet, example) => {
    const el = bodyRef.current;
    setForm((prev) => {
      const current = prev.bodyText || "";
      let start = current.length;
      let end = current.length;
      if (el && typeof el.selectionStart === "number") {
        start = el.selectionStart;
        end = el.selectionEnd;
      }
      const next = current.slice(0, start) + snippet + current.slice(end);
      const key = extractVariables(snippet)[0];
      const examples = { ...prev.bodyExamples };
      if (key && example && !examples[key]) examples[key] = example;
      window.setTimeout(() => {
        if (el) {
          el.focus();
          const pos = start + snippet.length;
          el.setSelectionRange(pos, pos);
        }
      }, 0);
      return { ...prev, bodyText: next, bodyExamples: examples };
    });
  };

  /** Envolve a seleção do corpo com o marcador (*, _, ~). */
  const wrapSelection = (mark) => {
    const el = bodyRef.current;
    setForm((prev) => {
      const current = prev.bodyText || "";
      let start = current.length;
      let end = current.length;
      if (el && typeof el.selectionStart === "number") {
        start = el.selectionStart;
        end = el.selectionEnd;
      }
      const selected = current.slice(start, end);
      const next = `${current.slice(0, start)}${mark}${selected}${mark}${current.slice(end)}`;
      window.setTimeout(() => {
        if (el) {
          el.focus();
          const pos = selected ? end + mark.length * 2 : start + mark.length;
          el.setSelectionRange(pos, pos);
        }
      }, 0);
      return { ...prev, bodyText: next };
    });
  };

  const insertVariableClick = (e) => {
    if (isNamed) {
      setVarName("");
      setVarAnchor(e.currentTarget);
      return;
    }
    insertIntoBody(`{{${nextPositionalIndex(form.bodyText)}}}`);
  };

  const insertNamedVariable = () => {
    const name = toSnakeCase(varName);
    if (!name) return;
    setVarAnchor(null);
    const suggestion = BILLING_VARIABLES.find((v) => v.name === name);
    insertIntoBody(`{{${name}}}`, suggestion ? suggestion.example : undefined);
  };

  const insertBillingVariable = (variable) => {
    if (isNamed) {
      insertIntoBody(`{{${variable.name}}}`, variable.example);
    } else {
      insertIntoBody(`{{${nextPositionalIndex(form.bodyText)}}}`, variable.example);
    }
  };

  const setBodyExample = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      bodyExamples: { ...prev.bodyExamples, [key]: value },
    }));
  };

  const handleSave = async () => {
    const errors = validateForm(form, i18n.t.bind(i18n));
    setClientErrors(errors);
    setApiError("");
    if (errors.length) {
      scrollTop();
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(form);
      const { data } = isExisting
        ? await api.put(`/meta-templates/${form.id}`, payload)
        : await api.post("/meta-templates", payload);
      toast.success(i18n.t(isExisting ? "metaTemplates.toasts.updated" : "metaTemplates.toasts.created"));
      if (onSaved) onSaved(data);
      if (onClose) onClose();
    } catch (err) {
      if (!isMountedRef.current) return;
      setApiError(apiErrorMessage(err, i18n.t("metaTemplates.toasts.saveError")));
      scrollTop();
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  };

  const counter = (value, max) => (
    <span className={`${classes.counter} ${value.length > max ? classes.counterOver : ""}`}>
      {value.length}/{max}
    </span>
  );

  const disabled = saving;

  return (
    <Dialog
      open={!!open}
      onClose={saving ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      classes={{ paper: classes.paper }}
      aria-labelledby="meta-template-editor-title"
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <div className={classes.titleWrap}>
          <h2 id="meta-template-editor-title" className={classes.titleText}>
            {isExisting ? tr("titleEdit", { name: form.name }) : tr("titleNew")}
          </h2>
          {isExisting && form.status && (
            <StatusChip status={form.status} reason={template ? template.metaRejectedReason : undefined} />
          )}
        </div>
        <IconButton size="small" edge="end" onClick={onClose} disabled={saving} aria-label={tr("close")}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content} style={{ padding: 0 }}>
        <div className={classes.formCol} ref={formColRef}>
          {apiError && (
            <Alert severity="error" className={classes.alert} onClose={() => setApiError("")}>
              <strong>{tr("metaError")}</strong> {apiError}
            </Alert>
          )}
          {clientErrors.length > 0 && (
            <Alert severity="warning" className={classes.alert} onClose={() => setClientErrors([])}>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {clientErrors.map((msg, idx) => (
                  <li key={idx}>{msg}</li>
                ))}
              </ul>
            </Alert>
          )}
          {showEditNotice && (
            <Alert severity="info" className={classes.alert}>
              {tr("editNotice")}
            </Alert>
          )}
          {showPendingNotice && (
            <Alert severity="warning" className={classes.alert}>
              {tr("pendingNotice")}
            </Alert>
          )}

          {/* Identificação */}
          <section className={classes.section}>
            <SectionHead classes={classes} title={tr("sections.basic")} help={tr("sections.basicHelp")} />
            <div className={classes.formGrid}>
              <Field classes={classes} className={classes.span3} label={tr("fields.whatsapp")} labelId="mt-whatsapp" required>
                <TextField
                  select
                  value={form.whatsappId || ""}
                  onChange={field("whatsappId")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  disabled={disabled || isExisting}
                  SelectProps={{ labelId: "mt-whatsapp" }}
                >
                  {(whatsapps || []).map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      {w.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Field>
              <Field
                classes={classes}
                className={classes.span3}
                label={tr("fields.name")}
                id="mt-name"
                required
                help={isLocked ? tr("fields.nameLocked") : tr("fields.nameHelp")}
              >
                <TextField
                  id="mt-name"
                  value={form.name}
                  onChange={(e) => set({ name: toSnakeCase(e.target.value) })}
                  inputProps={{ maxLength: LIMITS.name, style: { fontFamily: MONO } }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  disabled={disabled || isLocked}
                />
              </Field>
              <Field
                classes={classes}
                className={classes.span2}
                label={tr("fields.category")}
                labelId="mt-category"
                help={i18n.t(`metaTemplates.categoryHelp.${form.category}`)}
              >
                <TextField
                  select
                  value={form.category}
                  onChange={field("category")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={disabled}
                  SelectProps={{ labelId: "mt-category" }}
                >
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {i18n.t(`metaTemplates.category.${c}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Field>
              <Field classes={classes} className={classes.span2} label={tr("fields.language")} labelId="mt-language">
                <TextField
                  select
                  value={form.language}
                  onChange={field("language")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={disabled || isLocked}
                  SelectProps={{ labelId: "mt-language" }}
                >
                  {LANGUAGES.map((l) => (
                    <MenuItem key={l} value={l}>
                      {i18n.t(`metaTemplates.language.${l}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Field>
              <Field classes={classes} className={classes.span2} label={tr("fields.parameterFormat")} labelId="mt-format">
                <TextField
                  select
                  value={form.parameterFormat}
                  onChange={handleParameterFormat}
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={disabled}
                  SelectProps={{ labelId: "mt-format" }}
                >
                  {PARAMETER_FORMATS.map((p) => (
                    <MenuItem key={p} value={p}>
                      {i18n.t(`metaTemplates.parameterFormat.${p}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Field>
            </div>
          </section>

          {/* Cabeçalho */}
          <section className={classes.section}>
            <SectionHead classes={classes} title={tr("sections.header")} help={tr("sections.headerHelp")} />
            <div className={classes.formGrid}>
              <Field classes={classes} className={classes.span2} label={tr("fields.headerFormat")} labelId="mt-header">
                <TextField
                  select
                  value={form.headerFormat}
                  onChange={field("headerFormat")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={disabled}
                  SelectProps={{ labelId: "mt-header" }}
                >
                  {HEADER_FORMATS.map((h) => (
                    <MenuItem key={h} value={h}>
                      {i18n.t(`metaTemplates.headerFormat.${h}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Field>
              {form.headerFormat === "TEXT" && (
                <Field
                  classes={classes}
                  className={classes.span4}
                  label={tr("fields.headerText")}
                  id="mt-header-text"
                  counter={counter(form.headerText, LIMITS.headerText)}
                  help={tr("fields.headerTextHelp")}
                >
                  <TextField
                    id="mt-header-text"
                    value={form.headerText}
                    onChange={field("headerText")}
                    inputProps={{ maxLength: LIMITS.headerText }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={disabled}
                  />
                </Field>
              )}
              {form.headerFormat === "TEXT" && headerVar && (
                <Field
                  classes={classes}
                  className={classes.full}
                  label={tr("fields.headerVarExample", { variable: `{{${headerVar}}}` })}
                  id="mt-header-var"
                >
                  <TextField
                    id="mt-header-var"
                    value={form.headerVarExample}
                    onChange={field("headerVarExample")}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={disabled}
                  />
                </Field>
              )}
              {form.headerFormat !== "TEXT" && form.headerFormat !== "NONE" && (
                <Field
                  classes={classes}
                  className={classes.span4}
                  label={tr("fields.headerMediaExample")}
                  id="mt-header-media"
                  help={tr(`fields.headerMediaHelp.${form.headerFormat}`)}
                >
                  <TextField
                    id="mt-header-media"
                    value={form.headerExample}
                    onChange={field("headerExample")}
                    placeholder="https://"
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={disabled}
                  />
                </Field>
              )}
            </div>
          </section>

          {/* Corpo */}
          <section className={classes.section}>
            <SectionHead classes={classes} title={tr("sections.body")} help={tr("sections.bodyHelp")} />
            <div className={classes.stack}>
              <Field
                classes={classes}
                label={tr("fields.body")}
                id="mt-body"
                required
                counter={counter(form.bodyText, LIMITS.body)}
              >
                <div className={classes.toolbar}>
                  <Tooltip title={tr("format.bold")}>
                    <IconButton size="small" onClick={() => wrapSelection("*")} disabled={disabled} aria-label={tr("format.bold")}>
                      <FormatBoldIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={tr("format.italic")}>
                    <IconButton size="small" onClick={() => wrapSelection("_")} disabled={disabled} aria-label={tr("format.italic")}>
                      <FormatItalicIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={tr("format.strike")}>
                    <IconButton size="small" onClick={() => wrapSelection("~")} disabled={disabled} aria-label={tr("format.strike")}>
                      <StrikethroughSIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <span className={classes.toolbarSep} aria-hidden="true" />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={insertVariableClick}
                    disabled={disabled}
                  >
                    {tr("insertVariable")}
                  </Button>
                </div>
                <TextField
                  id="mt-body"
                  inputRef={bodyRef}
                  value={form.bodyText}
                  onChange={field("bodyText")}
                  placeholder={tr("fields.bodyPlaceholder")}
                  multiline
                  minRows={6}
                  maxRows={16}
                  inputProps={{ maxLength: LIMITS.body }}
                  variant="outlined"
                  fullWidth
                  required
                  disabled={disabled}
                />
              </Field>
              <Popover
                open={!!varAnchor}
                anchorEl={varAnchor}
                onClose={() => setVarAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <div className={classes.popover}>
                  <Field classes={classes} label={tr("variableName")} id="mt-var-name" help={tr("variableNameHelp")}>
                    <div className={classes.popoverRow}>
                      <TextField
                        id="mt-var-name"
                        autoFocus
                        value={varName}
                        onChange={(e) => setVarName(toSnakeCase(e.target.value))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            insertNamedVariable();
                          }
                        }}
                        inputProps={{ style: { fontFamily: MONO } }}
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                      <Button variant="contained" color="primary" onClick={insertNamedVariable} disabled={!varName}>
                        {tr("insert")}
                      </Button>
                    </div>
                  </Field>
                </div>
              </Popover>

              <div className={classes.field}>
                <div className={classes.help}>{tr("billingVariables")}</div>
                <div className={classes.chips}>
                  {BILLING_VARIABLES.map((v) => (
                    <Chip
                      key={v.name}
                      size="small"
                      variant="outlined"
                      label={`{{${v.name}}}`}
                      className={classes.chip}
                      onClick={() => insertBillingVariable(v)}
                      disabled={disabled}
                    />
                  ))}
                </div>
              </div>

              {bodyVars.length > 0 && (
                <div className={classes.field}>
                  <div className={classes.label}>{tr("examplesTitle")}</div>
                  <div className={classes.examplesGrid}>
                    {bodyVars.map((v) => (
                      <Field classes={classes} key={v} label={`{{${v}}}`} id={`mt-example-${v}`} mono required>
                        <TextField
                          id={`mt-example-${v}`}
                          value={form.bodyExamples[v] || ""}
                          onChange={setBodyExample(v)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          required
                          disabled={disabled}
                        />
                      </Field>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Rodapé */}
          <section className={classes.section}>
            <SectionHead classes={classes} title={tr("sections.footer")} help={tr("sections.footerHelp")} />
            <Field
              classes={classes}
              label={tr("fields.footer")}
              id="mt-footer"
              counter={counter(form.footerText, LIMITS.footer)}
            >
              <TextField
                id="mt-footer"
                value={form.footerText}
                onChange={field("footerText")}
                inputProps={{ maxLength: LIMITS.footer }}
                variant="outlined"
                size="small"
                fullWidth
                disabled={disabled}
              />
            </Field>
          </section>

          {/* Botões */}
          <section className={classes.section}>
            <SectionHead classes={classes} title={tr("sections.buttons")} help={tr("sections.buttonsHelp")} />
            <ButtonsEditor
              buttons={form.buttons}
              onChange={(buttons) => set({ buttons })}
              disabled={disabled}
            />
          </section>
        </div>

        <aside className={classes.previewCol}>
          <p className={classes.previewTitle}>{tr("previewTitle")}</p>
          <PhonePreview form={form} businessName={selectedWhatsapp ? selectedWhatsapp.name : ""} />
        </aside>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {tr("cancel")}
        </Button>
        <div className={classes.saveWrapper}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {isExisting ? tr("saveEdit") : tr("submit")}
          </Button>
          {saving && <CircularProgress size={20} className={classes.progress} />}
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default MetaTemplateEditor;
